import { useEffect } from 'react';
import { answeredSections } from './form-layout';

/** Characters Windows or POSIX reject in a filename. */
const ILLEGAL_IN_FILENAME = /[\\/:*?"<>|]/g;

/**
 * What a saved PDF should be called: `<form title>_<reference>`, the reference being the
 * case's ticket number — the same thing the person quotes when they call.
 *
 * Browsers take the default filename for "Save as PDF" from `document.title`, so this
 * is the only lever we have over it. Characters a filesystem would reject are replaced
 * rather than dropped, so two different titles cannot collapse into one name.
 */
export function printFileName(formTitle, reference) {
  const title = formTitle.replace(ILLEGAL_IN_FILENAME, ' ').replace(/\s+/g, ' ').trim();
  return `${title}_${reference}`;
}

/**
 * A read-only copy of what was just submitted — the submitter's record of it.
 *
 * Built from the same form definition that collected the answers, so labels, section
 * bands and option wording match the form exactly rather than being maintained twice.
 *
 * It shows only what was asked and answered: a hire with no IT requirements was never
 * shown those twelve questions, so they do not appear, and a blank optional field is
 * left out rather than printed as an empty row. Nothing here can be mistaken for an
 * answer nobody gave.
 *
 * Nothing is stored anywhere, so this exists only while the page is open. Printing (or
 * saving to PDF) is the only durable copy until there is an API to fetch one back.
 */
export function SubmissionReceipt({ form, payload, result, submittedBy }) {
  const sections = answeredSections(form, payload);

  // A dry run has no ticket number and no Dynamics timestamp: nothing was written. The
  // receipt says so where the reference would be, so a saved PDF can never pass for a
  // filed request.
  const dryRun = result.dryRun === true;
  const reference = dryRun ? 'DRY RUN' : result.ticketNumber;
  const received = dryRun ? new Date() : new Date(result.createdOn);

  // The browser names a saved PDF after the document title, and reads it when the print
  // dialog opens. Setting it for as long as the receipt is on screen — rather than
  // around our own button — means Ctrl+P produces the same filename, with no race
  // against when the browser happens to look.
  useEffect(() => {
    const previous = document.title;
    document.title = printFileName(form.title, dryRun ? 'dry-run' : result.ticketNumber);
    return () => {
      document.title = previous;
    };
  }, [form.title, dryRun, result.ticketNumber]);

  return (
    <article className="receipt">
      <header className="receipt-head">
        <h1>{form.title}</h1>
        <dl className="facts">
          <dt>Reference</dt>
          <dd>
            <code>{reference}</code>
          </dd>
          <dt>{dryRun ? 'Checked' : 'Received'}</dt>
          <dd>{received.toLocaleString()}</dd>
          {submittedBy && (
            <>
              <dt>Submitted by</dt>
              <dd>{submittedBy}</dd>
            </>
          )}
          <dt>Form version</dt>
          <dd>
            <code>
              {form.id} {form.version}
            </code>
          </dd>
          {/* The GUID a technician needs to open the case directly; small because
              nobody reads one aloud. A dry run has none. */}
          {!dryRun && (
            <>
              <dt className="muted">Case ID</dt>
              <dd className="muted">
                <code>{result.caseId}</code>
              </dd>
            </>
          )}
        </dl>
      </header>

      {sections.map((group) => (
        <section className="receipt-section" key={group.section ?? group.rows[0].hint.name}>
          {group.section && <h2>{group.section}</h2>}
          {group.rows.some((row) => row.value !== undefined) && (
            <dl className="answers">
              {group.rows
                .filter((row) => row.value !== undefined)
                .map(({ hint, value }) => (
                  <div className="answer" key={hint.name}>
                    <dt>{hint.label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
            </dl>
          )}
          {/* A list answer is a table: one row per entry, the columns as the form
              showed them, so the receipt reads like the sheet the submitter filled. */}
          {group.rows
            .filter((row) => row.table !== undefined)
            .map(({ hint, table }) => (
              <table className="answer-table" key={hint.name}>
                <caption>{hint.label}</caption>
                <thead>
                  <tr>
                    {table.columns.map((column) => (
                      <th scope="col" key={column.name}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((cells, index) => (
                    <tr key={index}>
                      {cells.map((cell, cellIndex) => (
                        <td key={table.columns[cellIndex].name}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
        </section>
      ))}
    </article>
  );
}
